/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2025-2026 (c) Miriam Mack, ISW University of Stuttgart (for umati and VDW e.V.)
 */

import Api from 'Api/Api';
import type { Company, UserClient } from 'Api/generated';
import { AllowedRoles, useAuth } from 'Contexts/Auth';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { BsKey, BsPencil, BsPeople, BsPlus, BsTrash } from 'react-icons/bs';
import Select from 'react-select';
import { getApiErrorMessage } from '../components/apiError';
import { FeedbackAlert, useFeedback } from '../components/Feedback';
import { passwordError } from '../components/password';
import RoleBadge from '../components/RoleBadges';
import styles from './Users.module.css';

type Role = 'CompanyMember' | 'Admin' | 'SuperAdmin';

const ROLE_LABELS: Record<Role, string> = {
  CompanyMember: 'Member',
  Admin: 'Admin',
  SuperAdmin: 'Super Admin',
};

const currentRole = (roles: string[] | null | undefined): Role => {
  if (roles?.includes('SuperAdmin')) return 'SuperAdmin';
  if (roles?.includes('Admin')) return 'Admin';
  return 'CompanyMember';
};

const sameIds = (a: string[] = [], b: string[] = []) => JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());

type UserSidebarProps = {
  users: UserClient[];
  selectedUserId: string | undefined;
  setSelectedUserId: (id: string | undefined) => void;
  onAdd: () => void;
};

const UserSidebar = ({ users, selectedUserId, setSelectedUserId, onAdd }: UserSidebarProps) => (
  <>
    <header className={styles['header-row']}>
      <h2 className="header">
        <BsPeople className="icon" />
        Users
      </h2>
    </header>
    <nav className={styles['users-overview']} aria-label="Users">
      <ul>
        {users.map((user) => (
          <li key={user.Id}>
            <button
              type="button"
              className={styles['user-button']}
              aria-current={selectedUserId === user.Id}
              onClick={() => setSelectedUserId(user.Id ?? undefined)}
            >
              {user.Username ?? user.Id}
            </button>
          </li>
        ))}
      </ul>
    </nav>
    <Button size="sm" variant="outline-primary" className="w-100 mt-2" onClick={onAdd}>
      <BsPlus /> Add user
    </Button>
  </>
);

type SetPasswordModalProps = {
  show: boolean;
  user: UserClient;
  onClose: () => void;
  onSaved: () => void;
};

const SetPasswordModal = ({ show, user, onClose, onSaved }: SetPasswordModalProps) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const close = () => {
    setPassword('');
    setConfirmPassword('');
    setSubmitted(false);
    setServerError(null);
    onClose();
  };

  const passwordProblem = passwordError(password);
  const confirmError = confirmPassword === password ? null : 'The passwords do not match.';
  const isValid = !passwordProblem && !confirmError;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setServerError(null);
    if (!isValid || !user.Id) return;
    setSubmitting(true);
    try {
      await Api.Users.putUser(user.Id, { ...user, Password: password });
      onSaved();
      close();
    } catch (err) {
      setServerError(getApiErrorMessage(err, 'The password could not be set.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={close}>
      <Modal.Header closeButton>
        <Modal.Title>Set new password</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Body>
          <p className="mb-3">
            Set a new password for <strong>{user.Username}</strong>.
          </p>

          <Form.Group className="mb-3" controlId="set-password">
            <Form.Label>New password</Form.Label>
            <Form.Control
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              isInvalid={submitted && !!passwordProblem}
              autoComplete="new-password"
              autoFocus
            />
            <Form.Control.Feedback type="invalid">{passwordProblem}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-2" controlId="set-password-confirm">
            <Form.Label>Confirm password</Form.Label>
            <Form.Control
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              isInvalid={(submitted || confirmPassword.length > 0) && !!confirmError}
              autoComplete="new-password"
            />
            <Form.Control.Feedback type="invalid">{confirmError}</Form.Control.Feedback>
          </Form.Group>

          {serverError && <div className="alert alert-danger py-2 mt-3 mb-0">{serverError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Set password'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

type UserDetailProps = {
  user: UserClient;
  allCompanies: Company[];
  onChange: (updated: UserClient) => void;
  onDelete: (id: string) => void;
};

const UserDetail = ({ user, allCompanies, onChange, onDelete }: UserDetailProps) => {
  const auth = useAuth();
  const isSuperAdmin = auth.user?.Roles?.includes('SuperAdmin') ?? false;
  const isSelf = auth.user?.Id === user.Id;

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [newUsername, setNewUsername] = useState(user.Username ?? '');
  const [editError, setEditError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>(user.CompanyIds ?? []);
  const [selectedRole, setSelectedRole] = useState<Role>(currentRole(user.Roles));
  const { feedback, success, error } = useFeedback();

  const trimmedUsername = newUsername.trim();
  const usernameChanged = trimmedUsername !== (user.Username ?? '').trim();
  const companiesChanged = !sameIds(user.CompanyIds ?? [], selectedCompanyIds);
  const roleChanged = selectedRole !== currentRole(user.Roles);

  const handleSubmitUsername = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user.Id || !trimmedUsername || !usernameChanged) return;
    setEditError(null);
    try {
      await Api.Users.putUser(user.Id, { ...user, Username: trimmedUsername });
      onChange({ ...user, Username: trimmedUsername });
      setShowEditDialog(false);
      success('Username updated.');
    } catch (err) {
      setEditError(getApiErrorMessage(err, 'The username could not be changed.'));
    }
  };

  const handleSaveCompanies = async () => {
    if (!user.Id) return;
    try {
      await Api.Users.putUser(user.Id, { ...user, CompanyIds: selectedCompanyIds });
      onChange({ ...user, CompanyIds: selectedCompanyIds });
      success('Companies updated.');
    } catch (err) {
      error(err, 'The companies could not be saved.');
    }
  };

  const handleSaveRole = async () => {
    if (!user.Id || !roleChanged) return;
    try {
      if (selectedRole === 'Admin') await Api.Users.setAdminRole(user.Id);
      else if (selectedRole === 'SuperAdmin') await Api.Users.setSuperAdminRole(user.Id);
      else await Api.Users.setMemberRole(user.Id);
      onChange({ ...user, Roles: [selectedRole] });
      success('Role updated.');
    } catch (err) {
      error(err, 'The role could not be changed.');
    }
  };

  const handleDelete = async () => {
    if (!user.Id) return;
    setShowDeleteConfirm(false);
    try {
      await Api.Users.deleteUser(user.Id);
      onDelete(user.Id);
    } catch (err) {
      error(err, 'The user could not be deleted.');
    }
  };

  const companyOptions = allCompanies.map((c) => ({ value: c.Id ?? '', label: c.Name ?? c.Id ?? '' }));
  const roleOptions: Role[] = isSuperAdmin ? ['CompanyMember', 'Admin', 'SuperAdmin'] : ['CompanyMember', 'Admin'];
  const canChangeRole = !isSelf && (isSuperAdmin || currentRole(user.Roles) !== 'SuperAdmin');

  return (
    <>
      <div className={styles['name-header']}>
        <h4 className="mb-0">{user.Username}</h4>
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-primary" onClick={() => setShowEditDialog(true)}>
            <BsPencil className="me-1" />
            Edit
          </Button>
          <Button size="sm" variant="outline-primary" onClick={() => setShowSetPassword(true)}>
            <BsKey className="me-1" />
            Set password
          </Button>
        </div>
      </div>
      <p className={styles['name-subheading']}>ID: {user.Id}</p>

      <FeedbackAlert feedback={feedback} />

      <SetPasswordModal
        show={showSetPassword}
        user={user}
        onClose={() => setShowSetPassword(false)}
        onSaved={() => success('Password updated.')}
      />

      <Modal
        show={showEditDialog}
        onHide={() => {
          setShowEditDialog(false);
          setEditError(null);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit username</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitUsername} noValidate>
          <Modal.Body>
            <Form.Group controlId="edit-username">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                isInvalid={!!editError || (newUsername.length > 0 && !trimmedUsername)}
                autoFocus
              />
              <Form.Control.Feedback type="invalid">{editError ?? 'Please enter a username.'}</Form.Control.Feedback>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!trimmedUsername || !usernameChanged}>
              Save
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Table bordered size="sm" className={styles.table}>
        <tbody>
          <tr>
            <th>Companies</th>
            <td>
              <div className="d-flex gap-2 align-items-center">
                <div style={{ flex: 1 }}>
                  <Select
                    classNamePrefix="rs"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
                    isMulti
                    placeholder="Select companies…"
                    options={companyOptions}
                    value={companyOptions.filter((o) => selectedCompanyIds.includes(o.value))}
                    onChange={(selected) => setSelectedCompanyIds(selected.map((s) => s.value))}
                  />
                </div>
                <Button size="sm" variant="primary" onClick={handleSaveCompanies} disabled={!companiesChanged}>
                  Save
                </Button>
              </div>
            </td>
          </tr>
          <tr>
            <th>Role</th>
            <td>
              {canChangeRole ? (
                <div className="d-flex gap-2 align-items-center">
                  <Form.Select
                    size="sm"
                    style={{ maxWidth: 220 }}
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value as Role)}
                    aria-label="Role"
                  >
                    {roleOptions.map((role) => (
                      <option key={role} value={role}>
                        {ROLE_LABELS[role]}
                      </option>
                    ))}
                  </Form.Select>
                  <Button size="sm" variant="primary" onClick={handleSaveRole} disabled={!roleChanged}>
                    Save
                  </Button>
                </div>
              ) : (
                <div className="d-flex gap-2 align-items-center">
                  {user.Roles?.map((r) => (
                    <RoleBadge key={r} role={r} />
                  ))}
                  <span className="text-muted small">
                    {isSelf ? 'You cannot change your own role.' : 'Only a Super Admin can change this role.'}
                  </span>
                </div>
              )}
            </td>
          </tr>
        </tbody>
      </Table>

      <div className="d-flex mt-4">
        <Button
          size="sm"
          variant="outline-danger"
          className="ms-auto"
          onClick={() => setShowDeleteConfirm(true)}
          disabled={isSelf}
          title={isSelf ? 'You cannot delete your own account' : undefined}
        >
          <BsTrash className="me-1" />
          Delete user
        </Button>
      </div>

      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete user</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Do you really want to delete <strong>{user.Username}</strong>? This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

type AddUserModalProps = {
  show: boolean;
  allCompanies: Company[];
  onClose: () => void;
  onCreated: (user: UserClient) => void;
};

const AddUserModal = ({ show, allCompanies, onClose, onCreated }: AddUserModalProps) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [companyIds, setCompanyIds] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setUsername('');
    setPassword('');
    setConfirmPassword('');
    setCompanyIds([]);
    setSubmitted(false);
    setServerError(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const usernameError = username.trim() ? null : 'Please enter a username.';
  const passwordProblem = passwordError(password);
  const confirmError = confirmPassword === password ? null : 'The passwords do not match.';
  const isValid = !usernameError && !passwordProblem && !confirmError;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setServerError(null);
    if (!isValid) return;
    setSubmitting(true);
    try {
      const { data: created } = await Api.Users.postUser({
        Username: username.trim(),
        Password: password,
        CompanyIds: companyIds,
      });
      onCreated(created);
      close();
    } catch (err) {
      setServerError(getApiErrorMessage(err, 'The user could not be created.'));
    } finally {
      setSubmitting(false);
    }
  };

  const companyOptions = allCompanies.map((c) => ({ value: c.Id ?? '', label: c.Name ?? c.Id ?? '' }));

  return (
    <Modal show={show} onHide={close}>
      <Modal.Header closeButton>
        <Modal.Title>Add user</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="add-username">
            <Form.Label>Username</Form.Label>
            <Form.Control
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              isInvalid={submitted && !!usernameError}
              autoComplete="off"
              autoFocus
            />
            <Form.Control.Feedback type="invalid">{usernameError}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="add-password">
            <Form.Label>Password</Form.Label>
            <Form.Control
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              isInvalid={submitted && !!passwordProblem}
              autoComplete="new-password"
            />
            <Form.Control.Feedback type="invalid">{passwordProblem}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="add-password-confirm">
            <Form.Label>Confirm password</Form.Label>
            <Form.Control
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              isInvalid={(submitted || confirmPassword.length > 0) && !!confirmError}
              autoComplete="new-password"
            />
            <Form.Control.Feedback type="invalid">{confirmError}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>Companies</Form.Label>
            <Select
              classNamePrefix="rs"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
              isMulti
              placeholder="Select companies (optional)…"
              options={companyOptions}
              value={companyOptions.filter((o) => companyIds.includes(o.value))}
              onChange={(selected) => setCompanyIds(selected.map((s) => s.value))}
            />
          </Form.Group>

          {serverError && <div className="alert alert-danger py-2 mt-3 mb-0">{serverError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Adding…' : 'Add user'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const UserPage = () => {
  const [users, setUsers] = useState<UserClient[]>([]);
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);
  const [showAddUser, setShowAddUser] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    Api.Users.getUsers()
      .then(({ data }) => {
        const sorted = [...data].sort((a, b) => (a.Username ?? '').localeCompare(b.Username ?? ''));
        setUsers(sorted);
        if (sorted.length > 0) setSelectedUserId(sorted[0].Id ?? undefined);
      })
      .catch((err) => setLoadError(getApiErrorMessage(err, 'The users could not be loaded.')));

    Api.Company.getCompanies()
      .then(({ data }) => setAllCompanies(data))
      .catch(() => setAllCompanies([]));
  }, []);

  const selectedUser = users.find((u) => u.Id === selectedUserId);

  const handleChange = (updated: UserClient) => {
    setUsers((prev) => prev.map((u) => (u.Id === updated.Id ? updated : u)));
  };

  const handleDelete = (id: string) => {
    setUsers((prev) => {
      const remaining = prev.filter((u) => u.Id !== id);
      setSelectedUserId((current) => (current === id ? (remaining[0]?.Id ?? undefined) : current));
      return remaining;
    });
  };

  const handleCreated = (created: UserClient) => {
    setUsers((prev) => [...prev, created].sort((a, b) => (a.Username ?? '').localeCompare(b.Username ?? '')));
    setSelectedUserId(created.Id ?? undefined);
  };

  return (
    <AllowedRoles roles={['SuperAdmin', 'Admin']}>
      <div className={styles['users-page']}>
        <aside className={styles['users-sidebar']}>
          <UserSidebar
            users={users}
            selectedUserId={selectedUserId}
            setSelectedUserId={setSelectedUserId}
            onAdd={() => setShowAddUser(true)}
          />
        </aside>

        <main className={styles['users-content']}>
          {loadError && <div className="alert alert-danger">{loadError}</div>}

          {!loadError && users.length === 0 && (
            <div className="text-muted">
              <h5>No users yet</h5>
              <p>Use "Add user" to create the first user.</p>
            </div>
          )}

          {selectedUser && (
            <UserDetail
              key={selectedUser.Id}
              user={selectedUser}
              allCompanies={allCompanies}
              onChange={handleChange}
              onDelete={handleDelete}
            />
          )}
        </main>
      </div>

      <AddUserModal
        show={showAddUser}
        allCompanies={allCompanies}
        onClose={() => setShowAddUser(false)}
        onCreated={handleCreated}
      />
    </AllowedRoles>
  );
};

export default UserPage;
